import { parseBlob } from 'music-metadata'
import { MediaMetadata, MediaType } from '@/types/media'

/**
 * Extract metadata from audio or video files
 */
export async function extractMediaMetadata(file: File): Promise<MediaMetadata> {
  try {
    const metadata = await parseBlob(file as any)
    
    const extractedMetadata: MediaMetadata = {
      title: metadata.common.title || file.name,
      artist: metadata.common.artist || 'Unknown Artist',
      album: metadata.common.album || 'Unknown Album',
      year: metadata.common.year?.toString(),
      genre: metadata.common.genre?.[0],
      duration: metadata.format.duration,
      bitrate: metadata.format.bitrate
    }

    // Handle cover art
    if (metadata.common.picture?.[0]) {
      const picture = metadata.common.picture[0]
      const base64String = btoa(String.fromCharCode(...picture.data))
      extractedMetadata.picture = `data:${picture.format};base64,${base64String}`
    }

    // Video-specific metadata
    if (file.type.startsWith('video/')) {
      // Try to extract video resolution if available
      if (metadata.format.numberOfChannels) {
        extractedMetadata.codec = metadata.format.codec
      }
    }

    return extractedMetadata
  } catch (error) {
    console.warn('Could not extract metadata:', error)
    
    // Return fallback metadata
    return {
      title: file.name,
      artist: 'Unknown Artist',
      album: 'Unknown Album',
      duration: 0
    }
  }
}

/**
 * Extract video-specific metadata (for future enhancement with dedicated video metadata library)
 */
export async function extractVideoMetadata(file: File): Promise<MediaMetadata> {
  if (!file.type.startsWith('video/')) {
    throw new Error('File is not a video')
  }

  try {
    // For now, use the same music-metadata approach
    // In the future, this could be enhanced with a video-specific library
    const metadata = await extractMediaMetadata(file)
    
    // Create a video element to extract resolution
    return new Promise((resolve) => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      
      video.onloadedmetadata = () => {
        const enhancedMetadata: MediaMetadata = {
          ...metadata,
          resolution: {
            width: video.videoWidth,
            height: video.videoHeight
          },
          aspectRatio: `${video.videoWidth}:${video.videoHeight}`,
          framerate: 30 // Default, could be extracted with more sophisticated tools
        }
        
        URL.revokeObjectURL(video.src)
        resolve(enhancedMetadata)
      }
      
      video.onerror = () => {
        URL.revokeObjectURL(video.src)
        resolve(metadata) // Fallback to basic metadata
      }
      
      video.src = URL.createObjectURL(file)
    })
  } catch (error) {
    console.warn('Could not extract video metadata:', error)
    return extractMediaMetadata(file)
  }
}

/**
 * Determine media type from file MIME type and extension (enhanced)
 */
export function getMediaType(file: File): MediaType {
  const validation = validateFileTypeAndExtension(file)
  return validation.detectedType
}

/**
 * Legacy media type detection (fallback for basic MIME type checking)
 */
export function getMediaTypeFromMime(file: File): MediaType {
  if (file.type.startsWith('audio/')) {
    return 'audio'
  }
  
  if (file.type.startsWith('video/')) {
    return 'video'
  }
  
  if (file.type.startsWith('image/')) {
    return 'image'
  }
  
  return 'unknown'
}

/**
 * Format duration in seconds to MM:SS or HH:MM:SS format
 */
export function formatDuration(seconds: number): string {
  if (!seconds || isNaN(seconds)) {
    return '0:00'
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }
  
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

/**
 * File format validation mappings
 */
const SUPPORTED_FORMATS = {
  audio: {
    mimeTypes: [
      'audio/mp3',
      'audio/mpeg',
      'audio/wav',
      'audio/wave',
      'audio/x-wav',
      'audio/flac',
      'audio/x-flac',
      'audio/aac',
      'audio/mp4',
      'audio/x-m4a',
      'audio/m4a',
      'audio/ogg',
      'audio/vorbis',
      'audio/webm'
    ],
    extensions: [
      'mp3',
      'wav',
      'flac',
      'aac',
      'm4a',
      'ogg',
      'weba',
      'webm'
    ]
  },
  video: {
    mimeTypes: [
      'video/mp4',
      'video/x-msvideo',
      'video/avi',
      'video/quicktime',
      'video/x-quicktime',
      'video/mov',
      'video/webm',
      'video/ogg',
      'video/x-matroska',
      'video/mkv'
    ],
    extensions: [
      'mp4',
      'avi',
      'mov',
      'qt',
      'webm',
      'ogv',
      'mkv',
      'wmv'
    ]
  },
  image: {
    mimeTypes: [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/gif',
      'image/bmp',
      'image/tiff',
      'image/svg+xml'
    ],
    extensions: [
      'jpg',
      'jpeg',
      'png',
      'webp',
      'gif',
      'bmp',
      'tiff',
      'tif',
      'svg'
    ]
  }
}

/**
 * Enhanced file validation that checks both MIME type and extension
 */
export function validateFileTypeAndExtension(file: File): {
  isValid: boolean
  detectedType: MediaType
  mimeTypeValid: boolean
  extensionValid: boolean
  suggestedCorrection?: string
  error?: string
} {
  const extension = getFileExtension(file.name)
  const mimeType = file.type.toLowerCase()
  
  let detectedType: MediaType = 'unknown'
  let mimeTypeValid = false
  let extensionValid = false
  let suggestedCorrection: string | undefined
  
  // Check each format category
  for (const [formatType, formatData] of Object.entries(SUPPORTED_FORMATS)) {
    const isMimeMatch = formatData.mimeTypes.includes(mimeType)
    const isExtensionMatch = formatData.extensions.includes(extension)
    
    if (isMimeMatch || isExtensionMatch) {
      detectedType = formatType as MediaType
      mimeTypeValid = isMimeMatch
      extensionValid = isExtensionMatch
      
      // If only one matches, suggest correction
      if (isMimeMatch && !isExtensionMatch) {
        suggestedCorrection = `File extension .${extension} doesn't match MIME type ${mimeType}. Expected extensions: ${formatData.extensions.join(', ')}`
      } else if (!isMimeMatch && isExtensionMatch) {
        suggestedCorrection = `MIME type ${mimeType} doesn't match extension .${extension}. Expected MIME types: ${formatData.mimeTypes.join(', ')}`
      }
      
      break
    }
  }
  
  const isValid = mimeTypeValid && extensionValid && detectedType !== 'unknown'
  const error = !isValid ? 
    `Unsupported file format. MIME: ${mimeType}, Extension: .${extension}` : 
    undefined
  
  return {
    isValid,
    detectedType,
    mimeTypeValid,
    extensionValid,
    suggestedCorrection,
    error
  }
}

/**
 * Check if file is supported for media playback (enhanced version)
 */
export function isSupportedMediaFile(file: File): boolean {
  const validation = validateFileTypeAndExtension(file)
  return validation.isValid
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || ''
}

/**
 * Convert time in seconds to a human-readable format
 */
export function formatTimeRemaining(currentTime: number, duration: number): string {
  const remaining = duration - currentTime
  if (remaining <= 0) return '0:00'
  
  return `-${formatDuration(remaining)}`
}

/**
 * Calculate playback progress as percentage
 */
export function calculateProgress(currentTime: number, duration: number): number {
  if (!duration || duration === 0) return 0
  return Math.min(100, (currentTime / duration) * 100)
}

/**
 * Validate if file is suitable for DRM protection
 */
export function isDRMCompatible(file: File): boolean {
  // Based on existing patterns in the codebase
  return getMediaType(file) !== 'unknown' && isSupportedMediaFile(file)
}

/**
 * Get user-friendly error message for unsupported file formats
 */
export function getFileFormatErrorMessage(file: File): string {
  const validation = validateFileTypeAndExtension(file)
  const extension = getFileExtension(file.name)
  
  if (validation.isValid) {
    return ''
  }
  
  let message = `File format not supported:\n`
  message += `• File: ${file.name}\n`
  message += `• MIME Type: ${file.type}\n`
  message += `• Extension: .${extension}\n\n`
  
  // Provide helpful suggestions based on detected issues
  if (validation.suggestedCorrection) {
    message += `${validation.suggestedCorrection}\n\n`
  }
  
  message += `Supported formats:\n`
  message += `• Audio: MP3, WAV, FLAC, AAC, M4A, OGG\n`
  message += `• Video: MP4, AVI, MOV, WebM, MKV\n`
  message += `• Images: JPG, PNG, WebP, GIF`
  
  return message
}

/**
 * Get supported file extensions for display
 */
export function getSupportedExtensions(): {
  audio: string[]
  video: string[]
  image: string[]
  all: string[]
} {
  const audio = SUPPORTED_FORMATS.audio.extensions
  const video = SUPPORTED_FORMATS.video.extensions
  const image = SUPPORTED_FORMATS.image.extensions
  
  return {
    audio,
    video,
    image,
    all: [...audio, ...video, ...image]
  }
}